import React, {
	// eslint-disable-next-line jira/restricted/react
	PureComponent,
	type ComponentType,
	type ReactElement,
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
} from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import {
	TimelineModeOptions,
	type ColumnDuration,
	type TimelineDuration,
	type TimelineWindow,
	type TimelineMode,
} from '../../../../../model/timeline';
import { getCurrentMonth } from '../../../../../utils/dates.tsx';
import { getColumnPositions } from '../../../common/utils/columns.tsx';
import ColumnsOverlay from '../columns-overlay';
import {
	getTodayCoordinates,
	getColumnHours,
	getColumnWeekNumbers,
	getColumnMonthNumbers,
	getColumnQuarterNumbers,
	getColumnYearCaptions,
	isDateWithinTimelineDuration,
} from './common/utils';
import type { APIProps as DayCellProps } from './day/view.tsx';
import type { APIProps as MonthCellProps } from './month/view.tsx';
import type { APIProps as QuarterCellProps } from './quarter/view.tsx';
import type { APIProps as WeekCellProps } from './week/view.tsx';

const { DAYS, WEEKS, MONTHS, QUARTERS } = TimelineModeOptions;
type CellElement = ReactElement<ComponentProps<typeof Container>>;

type APIProps = {
	isHighlightToday: boolean;
	today: Date;
	timelineMode: TimelineMode;
	timelineDuration: TimelineDuration;
	timelineWindow: TimelineWindow;
	columnDurations: ColumnDuration[];
};

type Props = APIProps & {
	DayCell: ComponentType<DayCellProps>;
	WeekCell: ComponentType<WeekCellProps>;
	MonthCell: ComponentType<MonthCellProps>;
	QuarterCell: ComponentType<QuarterCellProps>;
};

// eslint-disable-next-line jira/react/no-class-components
class HeaderCells extends PureComponent<Props> {
	// go/jfe-eslint
	// eslint-disable-next-line react/sort-comp
	static whyDidYouRender = true;

	UNSAFE_componentWillReceiveProps(nextProps: Props) {
		if (this.props.today !== nextProps.today) {
			this.dayCellElement = undefined;
			this.weekCellElement = undefined;
			this.monthCellElement = undefined;
			this.quarterCellElement = undefined;
		}
	}

	dayCellElement: ReactElement[] | undefined;

	weekCellElement: ReactElement[] | undefined;

	monthCellElement: ReactElement[] | undefined;

	quarterCellElement: ReactElement[] | undefined;

	renderDayCells(columnPositions: [number, number][]): CellElement[] {
		const { today, columnDurations, DayCell, isHighlightToday } = this.props;
		const columnHours = getColumnHours(columnDurations);

		this.dayCellElement = columnPositions.map(([left, right]: [number, number], index) => (
			<Container key={index} left={left} right={right}>
				<DayCell
					isHighlightToday={isHighlightToday}
					hours={columnHours[index]}
					date={columnDurations[index].startDate}
					today={today}
				/>
			</Container>
		));

		return this.dayCellElement;
	}

	renderWeekCells(columnPositions: [number, number][]): CellElement[] {
		const { today, columnDurations, WeekCell, isHighlightToday, timelineDuration } = this.props;
		const todayCoordinates =
			isHighlightToday && isDateWithinTimelineDuration(today, timelineDuration)
				? getTodayCoordinates(today, columnDurations)
				: undefined;
		const columnWeekNumbers = getColumnWeekNumbers(columnDurations);
		const currentMonth = getCurrentMonth(today);
		const yearCaptions = getColumnYearCaptions(today, columnDurations);

		this.weekCellElement = columnPositions.map(([left, right]: [number, number], index) => (
			<Container key={index} left={left} right={right}>
				<WeekCell
					columnIndex={index}
					isHighlightToday={isHighlightToday}
					// @ts-expect-error - TS2322 - Type 'readonly [number, number] | undefined' is not assignable to type '[number, number] | undefined'.
					todayCoordinates={todayCoordinates}
					weekNumbers={columnWeekNumbers[index]}
					currentMonth={currentMonth}
					yearCaption={yearCaptions[index]}
				/>
			</Container>
		));

		return this.weekCellElement;
	}

	renderMonthCells(columnPositions: [number, number][]): CellElement[] {
		const { today, columnDurations, MonthCell } = this.props;
		const columnMonthNumbers = getColumnMonthNumbers(columnDurations);
		const yearCaptions = getColumnYearCaptions(today, columnDurations);

		this.monthCellElement = columnPositions.map(([left, right]: [number, number], index) => (
			<Container key={index} left={left} right={right}>
				<MonthCell monthNumber={columnMonthNumbers[index]} yearCaption={yearCaptions[index]} />
			</Container>
		));

		return this.monthCellElement;
	}

	renderQuarterCells(columnPositions: [number, number][]): CellElement[] {
		const { today, columnDurations, QuarterCell } = this.props;
		const columnQuarterNumbers = getColumnQuarterNumbers(columnDurations);
		const yearCaptions = getColumnYearCaptions(today, columnDurations);

		this.quarterCellElement = columnPositions.map(([left, right]: [number, number], index) => (
			<Container key={index} left={left} right={right}>
				<QuarterCell
					quarterNumber={columnQuarterNumbers[index]}
					yearCaption={yearCaptions[index]}
				/>
			</Container>
		));

		return this.quarterCellElement;
	}

	renderColumnsForMode() {
		const { timelineMode, timelineDuration, columnDurations } = this.props;
		const columnPositions = getColumnPositions(timelineDuration, columnDurations);
		if (timelineMode === DAYS) {
			return this.dayCellElement || this.renderDayCells(columnPositions);
		}
		if (timelineMode === WEEKS) {
			return this.weekCellElement || this.renderWeekCells(columnPositions);
		}
		if (timelineMode === MONTHS) {
			return this.monthCellElement || this.renderMonthCells(columnPositions);
		}
		if (timelineMode === QUARTERS) {
			return this.quarterCellElement || this.renderQuarterCells(columnPositions);
		}
		throw Error('mode not found');
	}

	render() {
		const { timelineWindow, timelineDuration, columnDurations } = this.props;
		return (
			<CellsContainer>
				<ColumnsOverlay
					timelineWindow={timelineWindow}
					timelineDuration={timelineDuration}
					columnDurations={columnDurations}
				/>
				{this.renderColumnsForMode()}
			</CellsContainer>
		);
	}
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CellsContainer = styled.div({
	position: 'relative',
	width: '100%',
	height: '100%',
});

const secondaryBorder = `1px solid ${token('color.border', colors.N40)}`;
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div<{ left: number; right: number }>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	...(props) => ({
		// IE11 and Edge truncates positions to two decimal places
		// so we should do so, to mitigate rounding error
		left: `${props.left.toFixed(2)}%`,
		right: `${props.right.toFixed(2)}%`,
	}),
	position: 'absolute',
	top: '0',
	bottom: '0',
	flex: '0 0 auto',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'&:not(:last-of-type)': {
		borderRight: secondaryBorder,
	},
	boxSizing: 'border-box',
});

export default HeaderCells;
export type { APIProps };
